import queryApi from 'api/query';
import { CancelToken } from 'axios';
import { plainToClass } from 'class-transformer';
import { clearCompanyDetailsCache } from '../../api-utils';
import {
  CreateRefundRequest,
  FlockRenewalCartRequest,
  FlockSubmissionCartRequest,
  FreePaymentRequest,
  OrderCartResponseModel,
  OrderCompletedResponseModel,
  OrderInvoiceModel,
  RequestRefundModel,
} from './models';

interface StripePaymentRequest {
  CartId: number;
  SourceToken: string;
}

/**
 * Handles all requests to the `/purchase` resource.
 */
class PurchasingApi {
  private resource = 'purchase';

  /**
   * Requests a cart for flock renewals.
   */
  getCartForFlockRenewal = async (
    request: FlockRenewalCartRequest,
    token?: CancelToken,
  ): Promise<OrderCartResponseModel> => {
    const response = await queryApi.post(
      `/${this.resource}/cart/renewal`,
      request,
      { cancelToken: token },
    );
    return plainToClass(OrderCartResponseModel, response.data);
  }

  /**
   * Requests a cart for flock activations.
   */
  getCartForFlockActivation = async (
    request: FlockRenewalCartRequest,
    token?: CancelToken,
  ): Promise<OrderCartResponseModel> => {
    const response = await queryApi.post(
      `/${this.resource}/cart/activation`,
      request,
      { cancelToken: token },
    );
    return plainToClass(OrderCartResponseModel, response.data);
  }

  /**
   * Requests a cart for flock submissions.
   */
  getCartForFlockSubmission = async (
    request: FlockSubmissionCartRequest,
    token?: CancelToken,
  ): Promise<OrderCartResponseModel> => {
    const response = await queryApi.post(
      `/${this.resource}/cart/submission`,
      request,
      { cancelToken: token },
    );
    return plainToClass(OrderCartResponseModel, response.data);
  }

  /**
   * Pays for a cart using Stripe.
   */
  payByStripe = async (
    cartId: number,
    sourceToken: string,
    token?: CancelToken,
    companyId?: number,
  ): Promise<OrderCompletedResponseModel> => {
    const payload: StripePaymentRequest = {
      CartId: cartId,
      SourceToken: sourceToken,
    };

    const response = await queryApi.post(
      `/${this.resource}/pay/stripe`,
      payload,
      { cancelToken: token },
    );

    const result = plainToClass(OrderCompletedResponseModel, response.data);

    // If a companyId was supplied, clear company cache so details are refreshed
    if (companyId) {
      try {
        await clearCompanyDetailsCache(companyId);
      } catch (e) {
        console.warn('Failed to clear company cache after payByStripe:', e);
      }
    }

    return result;
  }

  /**
   * Completes a free payment for a cart.
   */
  payFree = async (
    cartId: number,
    token?: CancelToken,
    companyId?: number,
  ): Promise<OrderCompletedResponseModel> => {
    const payload = new FreePaymentRequest();
    payload.CartId = cartId;

    const response = await queryApi.post(
      `/${this.resource}/pay/free`,
      payload,
      { cancelToken: token },
    );

    const result = plainToClass(OrderCompletedResponseModel, response.data);

    // If a companyId was supplied, clear company cache so details are refreshed
    if (companyId) {
      try {
        await clearCompanyDetailsCache(companyId);
      } catch (e) {
        console.warn('Failed to clear company cache after payFree:', e);
      }
    }

    return result;
  }

  /**
   * Initiates a refund request for an order.
   */
  initiateRefund = async (
    orderId: number,
    token?: CancelToken,
  ): Promise<RequestRefundModel> => {
    const response = await queryApi.get(
      `/${this.resource}/refund/initiate/${orderId}`,
      { cancelToken: token },
    );

    return plainToClass(RequestRefundModel, response.data);
  }

  /**
   * Submits a refund request for an order.
   */
  submitRefund = async (
    request: CreateRefundRequest,
    token?: CancelToken,
  ): Promise<void> => {
    await queryApi.post(
      `/${this.resource}/refund`,
      request,
      { cancelToken: token },
    );
  }

  /**
   * Retrieves an invoice for a specific order.
   */
  getInvoice = async (
    orderId: number,
    token?: CancelToken,
  ): Promise<OrderInvoiceModel> => {
    const response = await queryApi.get(
      `/${this.resource}/order/${orderId}/invoice`,
      { cancelToken: token },
    );

    return plainToClass(OrderInvoiceModel, response.data);
  }

  /**
   * Checks if a submission is paid.
   */
  getSubmissionIsPaid = async (
    submissionId: number,
    token?: CancelToken,
  ): Promise<boolean> => {
    const response = await queryApi.get(
      `/${this.resource}/submission/${submissionId}/ispaid`,
      { cancelToken: token },
    );

    return response.data.isPaid;
  }
}

export default new PurchasingApi();
